import { Component, Prop, Ref, Watch } from "@eastsideco/salvo-ts";
import { ThemeComponent } from "@/theme/ThemeComponent";
import { cart } from "@/theme/Cart";
import { getQuantityMax, formatPrice} from "@/utils/products";
import type { ProductOptionPicker } from "./ProductOptionPicker";
import type { QuantitySpinner } from "../QuantitySpinner/QuantitySpinner";
import type { DrawerContainer } from '../Drawer/DrawerContainer';

import "./ProductForm.scss"

/**
 * A form which allows customers to select product options and then
 * add the matching variant and quantity to the cart.
 */
@Component
export class ProductForm extends ThemeComponent {
    @Prop() product!: ShopifyProduct;
    @Prop() optionStyle!: 'dropdown'|'buttons';
    @Prop() variantsQty!: string;
    @Prop() strings!: string;
    @Prop({ default: [] }) freeGifts!: number[];
    @Ref() options!: ProductOptionPicker;
    @Ref() singleProductSubmitButton!: HTMLButtonElement[];
    @Ref() error!: HTMLElement;
    @Ref() quantity!: QuantitySpinner|null;


    @Ref({required: false}) tests!: HTMLElement[];

    protected loading: boolean = false;
    protected selectedVariant: Shopify.Liquid.Variant|null = null;
    private quickAddDrawerContainer: DrawerContainer|null = null;
    protected quantityVal: number = 1;
    protected maxQty: string = '1';
    protected selectedCartQty: number = 0;

    private drawerContainer: DrawerContainer|null = null;
    private price!: HTMLDivElement|null;
    private productFeaturesJson!: string|null;

    init() {
        this.$subscribe(this.options.onSelectedVariantChange, (variant) => this.selectedVariant = variant);

        this.drawerContainer = document.querySelector('.side-cart-drawer');

        this.quickAddDrawerContainer = document.querySelector('.quick-add-drawer');

        this.selectedVariant = this.options.selectedVariant;

        // Quantity Funtions
        this.setQuantityMax();

        this.checkQuantityChange();

        this.price = document.querySelector('.product-main__details__price');

        const productFeatures = this.singleProductSubmitButton[0].getAttribute('data-features-json');
        if (productFeatures && productFeatures != '') {
            this.productFeaturesJson = productFeatures;
        }

        if (this.singleProductSubmitButton) {
            for (const button of this.singleProductSubmitButton) {
                this.$listen(button, 'click', async() => {

                    // Hide quick add if it is open.
                    if (this.quickAddDrawerContainer ) {
                        if (this.quickAddDrawerContainer.hasAttribute('open')) {
                            this.quickAddDrawerContainer?.close();
                        }
                    }

                    await this.addToCart(button);
                    this.drawerContainer?.open();
                });
            }
        }
    }

    protected showError(msg: string|null): void {
        this.error.textContent = msg;
        this.error.classList.add('is-active');
        this.error.style.display = msg ? '' : 'none';
    }

    @Watch('selectedVariant')
    protected handleVariantChanged() {

        /*
            Setting the variant param has been disabled here because
            at the time of writing it is not needed. Subscription roducts are
            currently grouped using product metafields, and variants do
            exist but are not used.
        */

        // setVariantUrlParam(this.selectedVariant?.id || null);


        this.setQuantityMax();
        this.handlePrice();
    }

    /**
     *  Update Price;
     * @returns
     */
    handlePrice() {
        if(this.selectedVariant ===  null) { return; }
        const moneyFormat = this.getString('moneyFormat');
        const updatedPrice = formatPrice(this.selectedVariant.price, moneyFormat);

        if (updatedPrice === null) { return; }
        if (this.price) {
            this.price.innerHTML = `<span class="fs-26">${updatedPrice}</div>`;
        }
    }

    /**
     * Set maximum variant quantity on variant change.
     */
    protected setQuantityMax() {
        this.maxQty = getQuantityMax(this.selectedVariant?.id || null, this.variantsQty);
        this.selectedCartQty = this.checkCartQty(this.selectedVariant);

        let maxQty = Number(this.maxQty);
        let inputValue = this.quantity?.input.value || '';
        let isOutOfStock = false;
        const continueSelling = this.quantity?.getAttribute('data-continue-selling');

        if (continueSelling == 'true') {
            maxQty = 99;
            inputValue = '1';
        }

        if (continueSelling == 'false' && Number(this.maxQty) <= 0) {
            isOutOfStock = true;
            this.handleOutOfStock();

            if (this.quantity) {
                this.quantity.decreaseButton.disabled = true;
                this.quantity.increaseButton.disabled = true;
            }

            inputValue = '0';
        }

        if (this.selectedCartQty > 0) {
            maxQty = maxQty - this.selectedCartQty;
        }

        this.handleQtyInput(maxQty, inputValue);
        this.handleQtyButtons(maxQty, inputValue);

        if (~~maxQty === 0) {
            this.quantity?.input.setAttribute('max', maxQty.toString());
            this.quantity?.classList.add('visually-hidden');
            return;
        }

        this.quantity?.input.setAttribute('max', maxQty.toString());
        this.quantity?.classList.remove('visually-hidden');

        if (!isOutOfStock) {
            this.handleInStock();
        }
    }

    handleQtyInput(maxQty: number, inputValue: string) {

        let qtyInput = this.quantity?.input.value;

        if (inputValue) {
            qtyInput = inputValue;
            if (this.quantity) {
                this.quantity.input.value = qtyInput.toString();
                this.quantityVal = Number(qtyInput);
            }
        }

        if (qtyInput && (~~qtyInput > ~~maxQty)) {
            if (!this.quantity) { return; }
            this.quantity.input.value = maxQty.toString();
            return;
        }
    }

    handleQtyButtons(maxQty: number, inputValue: string) {
        if (!this.quantity) { return; }

        if (~~inputValue >= ~~maxQty) {
            this.quantity.increaseButton.disabled = true;
            return;
        }

        this.quantity.increaseButton.disabled = false;
    }

    /**
     * Check if selected variant is in cart? get quanity.
     * @param variant - selected variant.
     * @returns
     */
    protected checkCartQty(variant: ShopifyVariant|null) {


        if (variant === null || cart.items.length === 0) { return 0; }

        let quantity = 0;
        for (const item of cart.items) {
            if (item.id === variant.id) {
                quantity = item.quantity;
            }
        }

        return quantity;
    }

    /**
     * If quantity switcher exists, update Quantity on value change.
     */
    protected checkQuantityChange() {
        if (!this.quantity) { return; }
        this.$subscribe(this.quantity.onChange, (value) => {
            this.quantityVal = value;
        });
    }

    /**
     * Handle Add to Cart.
     */
    protected async addToCart(button: HTMLButtonElement) {
        this.loading = true;

        const variant = this.selectedVariant;
        if (!variant) {
            this.showError(`Please select an option.`);
            return;
        }

        const propertyValue = button.dataset.bulletString;

        /* eslint-disable */
        const properties:{[key: string]: string} = {};

        const checkoutImageUrl:string|null = button.getAttribute('checkout_image') || "";
        if (checkoutImageUrl) {
            properties['_checkout_image'] = checkoutImageUrl;
        }
        
        properties['_display_quantity'] = "1";

        if (propertyValue && propertyValue !== "") {
            properties['_bullet_string'] = propertyValue;
        }

        if (this.productFeaturesJson) {
            properties['_features'] = this.productFeaturesJson;
        }

        const upsellId:string|null = button.getAttribute('upsell_id') || "";
        if (upsellId && upsellId != '') {
            properties['_upsell_id'] = upsellId;
        }

        const hideQty:string|null = button.getAttribute('hide_qty') || "";
        if (hideQty === "true") {
            properties['_hide_qty'] = hideQty;
        }

        const productItem = {
            id: variant.id,
            quantity: this.quantityVal,
            properties: properties
        };
    

        let giftIds = [];

        for (const freeGiftId of this.freeGifts) {
            giftIds.push({ 
                id: freeGiftId, 
                quantity: 1,
                properties: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    _eligibleProductId: String(variant.id)
                }
            })
        }

        let productWithGifts = [productItem, ...giftIds];

        /*
            Check if the product being added qualifies the user as an Absoluter.
            Products that apply are added in global settings.
            If a product being added qualifies the user, check if there are
            products in cart with associated absoluter products and if so,
            remove them and re-add their associated absoluter products
            in their place
        */
       if (this.selectedVariant?.title.includes('Supplement')) {
            const cartHasProductToUpgrade = cart.items.find((item) => {
                return Object.keys(item.properties).includes('_absoluter-product');
            });

            if (cartHasProductToUpgrade) {
                const cartItems = cart.items.map((item) => {
                    const absoluterProperty = Object.entries(item.properties).find((property) => {
                        return property[0] == '_absoluter-product';
                    });
                    return {
                        id: absoluterProperty ? Number(absoluterProperty[1]) : item.id, 
                        quantity: item.quantity,
                        properties // :item.properties?
                    };
                });

                cart.clear();
                productWithGifts = [...productWithGifts, ...cartItems]
            }

       }

        await cart.asInteraction(async () => {
           await cart.addItems(productWithGifts).catch((event: unknown | null) => {
                if (event instanceof Error) {
                    this.showError(event.message);
                }
            });
        });



        const straightToCheckout = button.getAttribute('straight-to-checkout') == 'true'
            ? true
            : false;

        // if global sidecart switch is 'off' re-direct to the cart page
        if (straightToCheckout) {
            window.location.href = '/checkout';
        } else {
            this.handleCartBtnCopy(button, this.quantityVal)
            return;
        }
    }

    /**
     * Max Quantity - update btn copy
     */

    protected handleCartBtnCopy(button: HTMLButtonElement, lastQtyAdded: number) {

        // how many of this product is in the cart?
        const checkQty = this.checkCartQty(this.selectedVariant) + lastQtyAdded;

        // how many are allowed in the cart?
        const allowedQty = button.getAttribute('data-max-product');

        // if required disable button... 
        if(allowedQty != null){
            const compareQty = checkQty >= parseInt(allowedQty)

            if (compareQty) {
                // disable button
                button.disabled = true;
                button.classList.add('button--disabled');

                // update copy
                const copy = button.getAttribute('disabledtext');
                const isBuyNow = button.getAttribute('straight-to-checkout');

                if (button.getAttribute('disabledtext') != null && copy && isBuyNow == 'false') {
                    button.innerHTML = copy;
                }
            }
        }
    }

    /**
     * Diasable AdToCart if variant is out of stock.
     */
    protected handleOutOfStock() {
        if (this.singleProductSubmitButton) {
            for (const button of this.singleProductSubmitButton) {
                button.disabled = true;
                button.classList.add('button--disabled');
            }
        }
    }

    /**
     * Enable AddToCart.
     */
    protected handleInStock() {
        for (const button of this.singleProductSubmitButton) {
            button.disabled = false;
            button.classList.remove('button--disabled');
            let btnText = this.getString('add');
            const isBuyNow = button.getAttribute('straight-to-checkout');

            // check that the amount in basket does not exceed the set 'allowed' amount
            // - mainly when dealing with free gifts...
            const maxExceeded = button.getAttribute('max-exceeded')

            if (maxExceeded != null && maxExceeded === 'true') {
                button.classList.add('button--disabled');
                button.disabled = true;
    
                // update copy
                const copy = button.getAttribute('disabledtext');

                if (button.getAttribute('disabledtext') != null && copy && isBuyNow === 'false') {
                    button.innerHTML = copy;
                }
            }
        }
    }

    /**
     * Get Language string set within the product template.
     * @param string
     * @returns
     */
    protected getString(string: string) {
        const strings = JSON.parse(this.strings);
        return strings[string];
    }
}